import {
  createContext,
  useContext,
  useEffect,
  useRef,
  type PropsWithChildren,
  type ReactNode,
} from 'react'
import {
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query'
import { Navigate, useLocation } from 'react-router-dom'
import { ApiError, apiRequest, ensureCsrfCookie, registerUnauthorizedHandler } from '../../lib/api'
import { fetchBusinessIacrmSettings, fetchOwnIacrmSettings } from '../settings/api'
import { clearIacrmConfig, saveIacrmConfig, setIacrmScope } from '../iacrm/api'
import { AuthSplitShellSkeleton } from './components/AuthSplitShell'
import { AppShellSkeleton } from '@/layouts/AppShellSkeleton'
import { StateSurfaceSkeleton } from '@/components/app/StateSurface'
import type {
  AuthEnvelope,
  AuthenticatedUser,
  LoginPayload,
} from '../../types/auth'

const authQueryKey = ['auth', 'session']

/** Paths that should never be a post-login redirect destination. */
const POST_LOGIN_BLOCKLIST = new Set([
  '/session-expired',
  '/403',
  '/account-suspended',
])

export function sanitizeRedirectPath(path: string | null): string {
  if (!path) return '/dashboard'
  const normalized = path.split('?')[0].split('#')[0]
  if (POST_LOGIN_BLOCKLIST.has(normalized)) return '/dashboard'
  return path
}

type AuthStatus = 'loading' | 'authenticated' | 'unauthenticated'

interface AuthSessionContextValue {
  status: AuthStatus
  isLoading: boolean
  isAuthenticated: boolean
  user: AuthenticatedUser | null
  loginPending: boolean
  logoutPending: boolean
  login: (payload: LoginPayload) => Promise<AuthenticatedUser>
  logout: () => Promise<void>
  hasPermission: (...permissionIds: string[]) => boolean
}

export const AuthSessionContext = createContext<AuthSessionContextValue | null>(null)

async function fetchCurrentUser() {
  try {
    const response = await apiRequest<AuthEnvelope>('/auth/me')
    return response.data
  } catch (error) {
    if (error instanceof ApiError && error.status === 401) {
      return null
    }

    throw error
  }
}

async function loginRequest(payload: LoginPayload) {
  await ensureCsrfCookie()

  const response = await apiRequest<AuthEnvelope>('/auth/login', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(payload),
  })

  return response.data
}

// eslint-disable-next-line react-refresh/only-export-components
export function isSuspendedAgentSession(user: AuthenticatedUser | null): boolean {
  return user?.status === 'suspended' && user.agent_profile?.status === 'suspended'
}

// eslint-disable-next-line react-refresh/only-export-components
export function isSuspendedSuperAdminSession(user: AuthenticatedUser | null): boolean {
  return (
    user?.status === 'suspended' &&
    (user.roles?.some((r) => r.slug === 'super-admin') ?? false)
  )
}

// eslint-disable-next-line react-refresh/only-export-components
export function isSuspendedSession(user: AuthenticatedUser | null): boolean {
  return user?.status === 'suspended'
}

async function logoutRequest() {
  try {
    await ensureCsrfCookie()
    await apiRequest<{ message: string }>('/auth/logout', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({}),
    })
  } catch (error) {
    if (error instanceof ApiError && error.status === 401) {
      return
    }

    throw error
  }
}

export function AuthSessionProvider({ children }: PropsWithChildren) {
  const queryClient = useQueryClient()

  const sessionQuery = useQuery({
    queryKey: authQueryKey,
    queryFn: fetchCurrentUser,
    retry: false,
  })

  const loginMutation = useMutation({
    mutationFn: loginRequest,
    onSuccess: (user) => {
      setIacrmScope(user.current_business_id ?? null)
      queryClient.setQueryData(authQueryKey, user)
    },
  })

  const logoutMutation = useMutation({
    mutationFn: logoutRequest,
    onSuccess: () => {
      setIacrmScope(null)
      queryClient.setQueryData(authQueryKey, null)
    },
    onSettled: async () => {
      await queryClient.invalidateQueries({
        queryKey: authQueryKey,
      })
    },
  })

  const user = sessionQuery.data ?? null
  const redirectingRef = useRef(false)

  useEffect(() => {
    registerUnauthorizedHandler(() => {
      if (redirectingRef.current) {
        return
      }
      const isAuthRoute = ['/login', '/session-expired', '/activate-invitation', '/verify-email', '/password/forgot', '/password/reset', '/legal', '/terms', '/privacy', '/cookies']
        .some((path) => window.location.pathname.startsWith(path))
      if (isAuthRoute) {
        return
      }
      redirectingRef.current = true
      setIacrmScope(null)
      queryClient.setQueryData(authQueryKey, null)

      try {
        void fetch('/api/auth/session-expired', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          credentials: 'include',
          keepalive: true,
          body: JSON.stringify({}),
        })
      } catch {
        // silent — don't block redirect
      }

      window.location.href = '/session-expired'
    })
  }, [queryClient])

  useEffect(() => {
    if (sessionQuery.isPending) {
      return
    }

    setIacrmScope(user?.current_business_id ?? null)

    if (user === null) {
      return
    }

    const canHydrateBusinessIacrm =
      user.current_business_id !== null &&
      (user.permissions.includes('settings.view-business') || user.permissions.includes('settings.update-business'))
    const canHydratePlatformIacrm =
      user.current_business_id === null &&
      (user.permissions.includes('settings.view-platform') || user.permissions.includes('settings.update-own'))

    if (!canHydrateBusinessIacrm && !canHydratePlatformIacrm) {
      clearIacrmConfig()
      return
    }

    let cancelled = false

    void (async () => {
      try {
        const response = canHydrateBusinessIacrm
          ? await fetchBusinessIacrmSettings()
          : await fetchOwnIacrmSettings()
        if (!cancelled) {
          saveIacrmConfig(response.data)
        }
      } catch {
        if (!cancelled) {
          clearIacrmConfig()
        }
      }
    })()

    return () => {
      cancelled = true
    }
  }, [sessionQuery.isPending, user])

  const status: AuthStatus = sessionQuery.isPending
    ? 'loading'
    : user === null
      ? 'unauthenticated'
      : 'authenticated'

  const permissionSet = new Set(user?.permissions ?? [])

  return (
    <AuthSessionContext.Provider
      value={{
        status,
        isLoading: status === 'loading',
        isAuthenticated: status === 'authenticated',
        user,
        loginPending: loginMutation.isPending,
        logoutPending: logoutMutation.isPending,
        login: loginMutation.mutateAsync,
        logout: logoutMutation.mutateAsync,
        hasPermission: (...permissionIds: string[]) =>
          permissionIds.some((permissionId) => permissionSet.has(permissionId)),
      }}
    >
      {children}
    </AuthSessionContext.Provider>
  )
}

// eslint-disable-next-line react-refresh/only-export-components
export function useAuthSession() {
  const context = useContext(AuthSessionContext)

  if (context === null) {
    throw new Error('useAuthSession must be used within AuthSessionProvider.')
  }

  return context
}

export function AuthEntryRedirect() {
  const { status, user } = useAuthSession()

  if (status === 'loading') {
    return <AuthSplitShellSkeleton />
  }

  if (status === 'authenticated') {
    return <Navigate to={isSuspendedSession(user) ? '/account-suspended' : '/dashboard'} replace />
  }

  return <Navigate to="/login" replace />
}

export function PublicOnly({ children }: { children: ReactNode }) {
  const { status, user } = useAuthSession()
  const location = useLocation()
  const isVerifiedEmailSuccess =
    location.pathname === '/verify-email' &&
    new URLSearchParams(location.search).get('verified') === '1'
  const isEmailVerificationAction =
    location.pathname === '/verify-email' &&
    new URLSearchParams(location.search).has('verification_url')

  if (status === 'loading') {
    return isVerifiedEmailSuccess || isEmailVerificationAction
      ? <StateSurfaceSkeleton variant="fullscreen" size="lg" />
      : <AuthSplitShellSkeleton />
  }

  if (isEmailVerificationAction) {
    return <>{children}</>
  }

  if (status === 'authenticated') {
    if (isSuspendedSession(user)) {
      return <Navigate to="/account-suspended" replace />
    }

    const redirectTo = new URLSearchParams(location.search).get('redirectTo')
    return <Navigate to={sanitizeRedirectPath(redirectTo)} replace />
  }

  return <>{children}</>
}

export function RequireAuth({ children }: { children: ReactNode }) {
  const { status, user } = useAuthSession()
  const location = useLocation()

  if (status === 'loading') {
    return <AppShellSkeleton />
  }

  if (status !== 'authenticated') {
    const redirectTo = `${location.pathname}${location.search}${location.hash}`

    return <Navigate to={`/login?redirectTo=${encodeURIComponent(redirectTo)}`} replace />
  }

  if (isSuspendedSession(user)) {
    return <Navigate to="/account-suspended" replace />
  }

  return <>{children}</>
}

export function PermissionBoundary({
  anyOf,
  children,
}: {
  anyOf: string[]
  children: ReactNode
}) {
  const { hasPermission } = useAuthSession()

  if (!hasPermission(...anyOf)) {
    return <Navigate to="/403" replace />
  }

  return <>{children}</>
}
